import React, { useState } from "react";
import { TextareaAutosize } from "@mui/base/TextareaAutosize";
import Select from "react-select";
import { connect } from "react-redux";

import { API_VOICES_PATIENTS } from "constants/routes";
import { convertToDt } from "utils/convertData";

import PageTemplate from "pages/PageTemplate";
import VoicesTabbedPageContent from "./VoicesTabbedPageContent";
import Typing from "../../components/Typing";

import "./styles.scss";

interface IExamplePost {
  permalink: string;
  text: string;
}
interface IAudienceResponse {
  response: string;
  examplePosts: IExamplePost[];
}

interface IBotOption {
  value: string;
  label: string;
}

interface IBotResult {
  bot: IBotOption;
  response?: IAudienceResponse;
  error?: boolean;
  loading: boolean;
}

function CompareVoicesScreen({ topic, authToken }) {
  const allBotOptions: IBotOption[] = topic.topicBots
    .map((tb) => tb.bot)
    .filter((b) => b.topicAudienceId)
    .map((b) => ({
      value: topic.audiences.find((a) => a.id == b.topicAudienceId).name,
      label: topic.audiences.find((a) => a.id == b.topicAudienceId).name,
    }));

  const [selectedBots, setSelectedBots] = useState<IBotOption[]>([]);
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<IBotResult[]>([]);

  const dt_from = new Date(topic.startDate);
  const initialFromDate = {
    year: dt_from.getFullYear(),
    month: dt_from.getMonth(),
  };
  const [fromDate, setFromDate] = useState<{
    value: string;
    label: string;
    payload: AtlasMach.IDate;
  }>(dateToOption(initialFromDate));
  const dt_to = new Date(topic.endtDate);
  const initialToDate = {
    year: dt_to.getFullYear(),
    month: dt_to.getMonth(),
  };
  const [toDate, setToDate] = useState<{
    value: string;
    label: string;
    payload: AtlasMach.IDate;
  }>(dateToOption(initialToDate));

  const isDateRangeValid =
    fromDate.payload.year < toDate.payload.year ||
    (fromDate.payload.year == toDate.payload.year &&
      fromDate.payload.month <= toDate.payload.month);

  const dateOptions = generateDateRange(initialFromDate, initialToDate).map(
    (d) => dateToOption(d)
  );

  const isSubmitDisabled =
    submitting || !isDateRangeValid || selectedBots.length < 2 || !question.trim();

  const submitQuestion = () => {
    if (isSubmitDisabled) return;

    const dt = convertToDt(fromDate.payload, toDate.payload);
    const initialResults: IBotResult[] = selectedBots.map((bot) => ({
      bot,
      loading: true,
    }));
    setResults(initialResults);
    setSubmitting(true);

    let pendingCount = selectedBots.length;

    selectedBots.forEach((bot) => {
      const apiUrl = API_VOICES_PATIENTS.replace("$1", topic.id);
      fetch(apiUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          topicId: topic.id,
          audience: bot.value,
          question: question,
          dateFrom: dt.dt_from,
          dateTo: dt.dt_to,
        }),
      })
        .then((res) => {
          if (res.status !== 200) {
            return { bot, error: true, loading: false } as IBotResult;
          }
          return res.json().then((jsonData) => ({
            bot,
            response: jsonData,
            loading: false,
          })) as Promise<IBotResult>;
        })
        .catch(() => ({ bot, error: true, loading: false }) as IBotResult)
        .then((completed) => {
          setResults((prev) =>
            prev.map((r) => (r.bot.value === bot.value ? completed : r))
          );
          pendingCount--;
          if (pendingCount === 0) setSubmitting(false);
        });
    });
  };

  const handleInputChange = (e) => {
    setQuestion(e.target.value.replace("\n", ""));
  };
  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && !isSubmitDisabled) submitQuestion();
  };

  return (
    <PageTemplate title="Voices">
      <VoicesTabbedPageContent tab="compare">
        <>
          <div className="voices-patients-screen-container">
            <div className="voices-patients-welcome-text">
              Compare responses from multiple voices over the period of{" "}
              <SelectDate
                options={dateOptions}
                value={fromDate}
                onChange={(d) => setFromDate(d)}
              />
              {" "}and{" "}
              <SelectDate
                options={dateOptions}
                value={toDate}
                onChange={(d) => setToDate(d)}
              />
              <br />
              <br />
              Ask me anything
            </div>

            <div className="voices-patients-body">
              <div className="voices-patients-body-left-margin"></div>
              <div className="voices-patients-question-answer">
                <div className="voices-compare-bot-select">
                  <Select
                    isMulti
                    options={allBotOptions}
                    value={selectedBots}
                    onChange={(selected) =>
                      setSelectedBots(selected as IBotOption[])
                    }
                    placeholder="Select 2 or more voices to compare..."
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: "rgba(8, 20, 27, 0.6)",
                        border: "1px solid #1C3A4A",
                        borderRadius: "14px",
                        color: "white",
                        minHeight: "50px",
                        fontSize: "20px",
                        paddingInline: "15px",
                        paddingBlock: "5px",
                        cursor: "text",
                      }),
                      menu: (base) => ({
                        ...base,
                        background: "#112936",
                        border: "1px solid #1C3A4A",
                        boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.45)",
                        borderRadius: "14px",
                        padding: "15px",
                        zIndex: 10,
                      }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isFocused ? "#1a3a4b" : "transparent",
                        color: "white",
                        cursor: "pointer",
                        padding: "10px 15px",
                        borderRadius: "8px",
                        fontSize: "18px",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        background: "rgba(120, 210, 241, 0.2)",
                        borderRadius: "8px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#78D2F1",
                        fontSize: "16px",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#78D2F1",
                        cursor: "pointer",
                        ":hover": {
                          background: "rgba(120, 210, 241, 0.3)",
                        },
                      }),
                      input: (base) => ({
                        ...base,
                        color: "white",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "#8D9CA6",
                      }),
                      clearIndicator: (base) => ({
                        ...base,
                        color: "#8D9CA6",
                        cursor: "pointer",
                        ":hover": { color: "white" },
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: "#8D9CA6",
                        cursor: "pointer",
                        ":hover": { color: "white" },
                      }),
                      indicatorSeparator: (base) => ({
                        ...base,
                        backgroundColor: "#1C3A4A",
                      }),
                    }}
                  />
                </div>

                <div className="voices-patients-question">
                  <TextareaAutosize
                    disabled={submitting}
                    className="voices-patients-question-input"
                    placeholder="Ask your question"
                    value={question}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                  />
                  <button
                    className={
                      "voices-patients-question-submit" +
                      (isSubmitDisabled ? " disabled" : "")
                    }
                    onClick={() => {
                      if (!isSubmitDisabled) submitQuestion();
                    }}
                  >
                    Submit
                  </button>
                </div>

                {results.length > 0 && (
                  <div className="voices-compare-results">
                    {results.map((result) => (
                      <CompareCard key={result.bot.value} result={result} />
                    ))}
                  </div>
                )}

                <div className="voices-patients-disclaimer">
                  This is an Artificial Intelligence engine that summarizes real
                  posts. Answers to individualized questions may vary.
                </div>
              </div>
              <div className="voices-patients-inspire-me"></div>
            </div>
          </div>
        </>
      </VoicesTabbedPageContent>
    </PageTemplate>
  );
}

function CompareCard({ result }: { result: IBotResult }) {
  const [showPosts, setShowPosts] = useState(false);

  if (result.loading) {
    return (
      <div className="voices-compare-card">
        <div className="voices-compare-card-header">{result.bot.label}</div>
        <Typing label={"typing"} />
      </div>
    );
  }

  if (result.error) {
    return (
      <div className="voices-compare-card">
        <div className="voices-compare-card-header">{result.bot.label}</div>
        <div className="voices-compare-card-body voices-compare-card-error">
          Sorry, something went wrong with this voice. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="voices-compare-card">
      <div className="voices-compare-card-header">{result.bot.label}</div>
      <div className="voices-compare-card-body">
        {result.response?.response}
      </div>
      {result.response?.examplePosts &&
        result.response.examplePosts.length > 0 && (
          <>
            <button
              className="voices-compare-card-toggle-posts"
              onClick={() => setShowPosts(!showPosts)}
            >
              {showPosts ? "Hide" : "Show"} example posts (
              {result.response.examplePosts.length})
            </button>
            {showPosts && (
              <div className="voices-compare-card-posts">
                {result.response.examplePosts.map((p) => (
                  <a
                    key={p.permalink}
                    href={p.permalink}
                    target="_blank"
                    className="voices-compare-card-post"
                  >
                    {p.text}
                  </a>
                ))}
              </div>
            )}
          </>
        )}
    </div>
  );
}

const SelectDate = ({ options, value, onChange }) => {
  return (
    <Select
      unstyled
      onChange={onChange}
      options={options}
      value={value}
      isSearchable={false}
      styles={{
        container: (baseStyle) => ({
          ...baseStyle,
          display: "inline-block",
        }),
        control: (baseStyle) => ({
          ...baseStyle,
          margin: "0",
          padding: "0 5px",
          border: "1px solid #1C3A4A",
          borderRadius: "15px",
          cursor: "pointer",
        }),
        indicatorsContainer: (baseStyle) => ({
          ...baseStyle,
          color: "#8D9CA6",
          marginLeft: "5px",
        }),
        menu: (baseStyle) => ({
          ...baseStyle,
          background: "#112936",
          border: "1px solid #1C3A4A",
          boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.45)",
          borderRadius: "14px",
          width: "max-content",
          minWidth: "100%",
          padding: "15px",
        }),
        option: (baseStyle, state) => ({
          ...baseStyle,
          margin: "10px 0",
          fontWeight: state.isFocused ? "bold" : "400",
          cursor: "pointer",
        }),
        singleValue: (baseStyle) => ({
          ...baseStyle,
          color: "#78D2F1",
          textDecoration: "underline",
          cursor: "pointer",
        }),
      }}
    />
  );
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function dateToOption(date: AtlasMach.IDate): {
  value: string;
  label: string;
  payload: AtlasMach.IDate;
} {
  return {
    value: `${MONTHS[date.month]} ${date.year}`,
    label: `${MONTHS[date.month]} ${date.year}`,
    payload: date,
  };
}

function generateDateRange(
  fromDate: AtlasMach.IDate,
  toDate: AtlasMach.IDate
): AtlasMach.IDate[] {
  let res: AtlasMach.IDate[] = [];
  for (var year = fromDate.year; year <= toDate.year; year++) {
    for (var month = 0; month <= 11; month++) {
      if (year == fromDate.year && month < fromDate.month) continue;
      if (year == toDate.year && month > toDate.month) continue;
      res.push({ year, month });
    }
  }
  return res;
}

const mapStateToProps = (state: AtlasMach.StoreState) => {
  if (!state.ui.topic)
    throw new Error("topic must be set to initialize this component");

  return {
    topic: state.ui.topic,
    authToken: state.data.auth_token,
  };
};

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(CompareVoicesScreen);
